import type { Timestamp } from 'firebase/firestore'
import type { TimingVendor } from './catalog'

/**
 * A circuit, as a record rather than a string.
 *
 * It began as a free-text field on an event and that was not enough for three
 * separate reasons, each of which needs a different attribute:
 *
 *  - **Weather** needs a location. A radar overlay cannot be positioned by name.
 *  - **Full course yellow** needs a length, to estimate what a lap costs under
 *    neutralisation.
 *  - **Matching** needs a stable identity. A live stream reports the circuit it
 *    is at, and that has to resolve to the same thing an event was catalogued
 *    against, or a capture matches nothing.
 *
 * Layout variants are separate tracks. Brands Hatch Indy and Brands Hatch GP
 * differ in length, so anything reasoning about lap times has to tell them
 * apart — and the vendors already name them separately.
 */
export interface TrackDoc {
  /** How the circuit is shown. The document id is the stable identifier. */
  name: string
  /**
   * Null until somebody fills it in.
   *
   * A track discovered by a scrape is created with what the scrape knows, which
   * is only a name — so the fields it cannot know start empty rather than
   * guessed, and the features that need them say so instead of drawing
   * something wrong. An absent location is a missing radar, not a radar over
   * the wrong circuit.
   */
  location: { lat: number; lng: number } | null
  /** Circuit length in metres. Null until known. */
  lengthM: number | null
  /**
   * The circuit's own timezone, which seeds an event's rather than replacing
   * it: an event keeps its own copy, because a meeting is occasionally run to
   * a different clock and the event is what sessions are entered against.
   */
  timezone: string
  /** ISO 3166-1 alpha-2, for grouping and for flags later. Null until known. */
  country: string | null
  /**
   * What each vendor calls this circuit, in their own words.
   *
   * The same idea as SeriesDoc.vendorNames and for the same reason: vendor
   * strings are what actually arrive, and they are not ours to choose. A stub
   * created from a scrape is seeded with the name that created it.
   */
  vendorNames: Partial<Record<TimingVendor, string[]>>
  createdAt: Timestamp
  updatedAt?: Timestamp
}

/**
 * Whether a track carries what the features that consume it need.
 *
 * Used to gate rather than to warn — a dashboard says "no location recorded for
 * this circuit" instead of failing oddly, and the admin list can show what is
 * still outstanding.
 */
export function trackIsComplete(track: Pick<TrackDoc, 'location' | 'lengthM'>): boolean {
  return track.location !== null && track.lengthM !== null
}

/** What a track is still missing, in the reader's terms. */
export function trackGaps(track: Pick<TrackDoc, 'location' | 'lengthM'>): string[] {
  const gaps: string[] = []
  if (track.location === null) gaps.push('location')
  if (track.lengthM === null) gaps.push('length')
  return gaps
}

/**
 * The identifier derived from a circuit name.
 *
 * Matches the Go implementation in the TSL scraper, which has to produce the
 * same id for the same circuit or a re-scrape would create a second record for
 * one place. Kept deliberately simple for that reason.
 */
export function trackIdFromName(name: string): string {
  const out = name
    .toLowerCase()
    // Decompose, then DROP the combining marks rather than letting them fall
    // through to the separator rule below. Turning them into separators would
    // make "Portimão" into "portima-o" and file it apart from "Portimao",
    // which is the split this collection exists to prevent.
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return out || 'unknown'
}
